
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useState } from "react";

type TeamMember = {
  name: string;
  role: string;
  bio: string;
  photo: string;
  linkedin?: string;
};

const teamMembers: TeamMember[] = [
  {
    name: "Erika Bondareva",
    role: "Co-Founder and CEO",
    bio: "Erika specialises in AI-driven audio diagnostics, with a PhD from the University of Cambridge in AI for cardiac and respiratory health. As the former CTO of a health tech AI startup, she has a track record of transforming research into real-world products. With a background in biomedical engineering and a passion for innovation, she leads auryx in bringing cutting-edge AI technology to healthcare.",
    photo: "/erika-256.webp",
    linkedin: "https://www.linkedin.com/in/erika-bondareva/"
  },
  {
    name: "Kayla-Jade Butkow",
    role: "Co-Founder and CTO",
    bio: "Kayla is an expert in AI model design and optimisation for in-ear health monitoring, with a PhD from the University of Cambridge focused on extracting health insights from earbuds. Her background combines biomedical and electrical engineering expertise, with experience at Nokia Bell Labs and startups in healthtech and hearing protection. She brings technical expertise in developing scalable, high-performance AI health monitoring solutions.",
    photo: "/kayla-256.webp",
    linkedin: "https://www.linkedin.com/in/kayla-jade-butkow/"
  },
  {
    name: "Cecilia Mascolo",
    role: "Co-Founder and CSO",
    bio: "Cecilia is a world leader in audio-based AI for health and a pioneer in wearable technology research. As a Professor at the University of Cambridge, she has shaped the field with groundbreaking work in AI-driven health monitoring. With an extensive network across academia, healthcare, and industry, she drives auryx's scientific vision and research partnerships.",
    photo: "/cecilia-256.webp",
    linkedin: "https://www.linkedin.com/in/cecilia-mascolo/"
  }
];

const TeamMemberImage = ({ src, name }: { src: string; name: string }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  
  const initials = name.split(' ').map(n => n[0]).join('');
  
  useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setHasError(true);
    
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);
  
  return (
    <div className="w-32 h-32 rounded-full overflow-hidden mb-6 relative bg-[#A4CBA6]/20 flex items-center justify-center ring-2 ring-[#A4CBA6]/40">
      {(!imageLoaded || hasError) && (
        <span className="text-xl font-semibold text-muted-foreground">{initials}</span>
      )}
      {!hasError && (
        <img 
          src={src} 
          alt={name} 
          width={256}
          height={256}
          loading="lazy"
          decoding="async"
          className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </div>
  );
};

export const TeamSection = () => {
  return (
    <section id="team" className="py-20 px-4 section-sage">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-8 text-foreground">Meet the Team</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {teamMembers.map((member) => (
            <a 
              key={member.name} 
              href={member.linkedin} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="block h-full"
              aria-label={`${member.name}'s LinkedIn profile`}
            >
              <Card className="h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 bg-white border border-[#A4CBA6]/30 hover:border-[#ffa05d]/50">
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <TeamMemberImage src={member.photo} name={member.name} />
                  <div className="w-full">
                    <h3 className="text-xl font-semibold mb-1 text-foreground">{member.name}</h3>
                    <p className="text-[#ffa05d] font-medium mb-4">{member.role}</p>
                    <p className="text-muted-foreground text-sm leading-relaxed">{member.bio}</p>
                  </div>
                </CardContent>
              </Card>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
